// Editor de zonas de alto riesgo: el cliente marca sobre la foto los lugares peligrosos de la obra
// (borde de losa, excavación, radio de grúa, sector de hormigonado…) y la meta de uso de casco.
// Tocá la foto para poner puntos; con 3 o más, "Terminar zona". Los puntos se guardan normalizados (0–1).
import React, { useRef, useState } from 'react';
import { X, Plus, Check, Undo2, Trash2, Loader2 } from 'lucide-react';
import { guardarConfig, errorSeguridad } from './seguridad.api';

const MAX_ZONAS = 12;
export const COLOR_NIVEL = { alto: '#dc2626', medio: '#f59e0b' };
const nuevoId = () => 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const clamp = (v) => Math.min(1, Math.max(0, v));
const SUGERENCIAS = ['Borde de losa', 'Excavación', 'Radio de grúa', 'Andamio', 'Acopio de hierros', 'Ingreso de camiones'];

export default function ZonasRiesgoEditor({ feedId, photo, url, config, onClose, onGuardado }) {
  const [zonas, setZonas] = useState(() => (config?.zonasRiesgo || []).map((z) => ({ ...z })));
  const [meta, setMeta] = useState(config?.metaCasco ?? 90);
  const [dibujo, setDibujo] = useState(null); // puntos [[x,y]]
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const svgRef = useRef(null);
  const W = photo?.w || 1920, H = photo?.h || 1080;

  const tocar = (e) => {
    if (!dibujo || dibujo.length >= 50) return;
    const r = svgRef.current.getBoundingClientRect();
    setDibujo([...dibujo, [clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height)]]);
  };
  const terminar = () => {
    if (!dibujo || dibujo.length < 3) return;
    const libre = SUGERENCIAS.find((s) => !zonas.some((z) => z.nombre === s));
    setZonas([...zonas, { id: nuevoId(), nombre: libre || `Zona de riesgo ${zonas.length + 1}`, puntos: dibujo, nivel: 'alto' }]);
    setDibujo(null);
  };
  const cambiar = (id, cambios) => setZonas(zonas.map((z) => (z.id === id ? { ...z, ...cambios } : z)));
  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const doc = await guardarConfig(feedId, { zonasRiesgo: zonas, metaCasco: Number(meta), obligatorios: config?.obligatorios });
      onGuardado(doc);
    } catch (e) { setError(errorSeguridad(e)); setGuardando(false); }
  };
  const pts = (l) => l.map(([x, y]) => `${x * W},${y * H}`).join(' ');
  const r = W / 140;

  return (
    <div onClick={onClose} className="fixed inset-0 z-[1000] bg-gray-900/50 flex items-center justify-center p-2 sm:p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col" data-seg-editor>
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">Zonas de alto riesgo y meta de casco</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Marcá en la foto los lugares peligrosos. Contamos cuánta gente pasa por ahí y si tiene casco. No cambia lo que detecta la IA.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto p-3 sm:p-5 grid gap-4 lg:grid-cols-[1fr_300px]">
          <div>
            <div className="relative w-full rounded-xl overflow-hidden bg-gray-900" style={{ aspectRatio: `${W} / ${H}` }}>
              {url ? <img src={url} alt="Última foto de la cámara" className="absolute inset-0 w-full h-full object-cover select-none" draggable={false} /> : <div className="absolute inset-0 grid place-items-center text-gray-400 text-sm">Cargando foto…</div>}
              <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={`absolute inset-0 w-full h-full ${dibujo ? 'cursor-crosshair' : ''}`} onClick={tocar}>
                {zonas.map((z) => (
                  <g key={z.id}>
                    <polygon points={pts(z.puntos)} fill={COLOR_NIVEL[z.nivel] + '40'} stroke={COLOR_NIVEL[z.nivel]} strokeWidth={r / 2.5} />
                    <text x={z.puntos[0][0] * W + r} y={z.puntos[0][1] * H - r} fill="#fff" fontSize={r * 2.2} fontWeight="700" stroke="#000" strokeWidth={r / 5} paintOrder="stroke">{z.nombre}</text>
                  </g>
                ))}
                {dibujo && (
                  <g>
                    <polyline points={pts(dibujo)} fill={dibujo.length >= 3 ? '#2563eb33' : 'none'} stroke="#2563eb" strokeWidth={r / 2.5} strokeDasharray={`${r} ${r / 2}`} />
                    {dibujo.map(([x, y], i) => <circle key={i} cx={x * W} cy={y * H} r={r} fill="#fff" stroke="#2563eb" strokeWidth={r / 3} />)}
                  </g>
                )}
              </svg>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {!dibujo ? (
                <button type="button" disabled={zonas.length >= MAX_ZONAS || !url} onClick={() => setDibujo([])} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-3 py-2 disabled:opacity-40">
                  <Plus size={15} />Nueva zona de riesgo
                </button>
              ) : (
                <>
                  <span className="text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2">{dibujo.length < 3 ? `Tocá la foto para marcar el contorno (${dibujo.length}/3 puntos)` : 'Seguí marcando o terminá la zona'}</span>
                  <button type="button" disabled={dibujo.length < 3} onClick={terminar} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold px-3 py-2 disabled:opacity-40"><Check size={15} />Terminar zona</button>
                  <button type="button" disabled={!dibujo.length} onClick={() => setDibujo(dibujo.slice(0, -1))} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 text-sm px-3 py-2 disabled:opacity-40"><Undo2 size={15} />Borrar punto</button>
                  <button type="button" onClick={() => setDibujo(null)} className="rounded-lg border border-gray-300 text-sm px-3 py-2">Cancelar</button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-700" htmlFor="seg-meta">Meta de uso de casco</label>
              <div className="flex items-center gap-2 mt-1">
                <input id="seg-meta" type="range" min={50} max={100} step={5} value={meta} onChange={(e) => setMeta(Number(e.target.value))} className="flex-1 accent-emerald-600" />
                <span className="w-12 text-right font-semibold text-gray-800">{meta}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Los días y horas debajo de la meta se marcan en rojo.</p>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-1">Zonas ({zonas.length}/{MAX_ZONAS})</div>
              {!zonas.length && <p className="text-xs text-gray-500">Todavía no hay zonas. Tocá "Nueva zona de riesgo".</p>}
              <ul className="space-y-2">
                {zonas.map((z) => (
                  <li key={z.id} className="border border-gray-200 rounded-xl p-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: COLOR_NIVEL[z.nivel] }} />
                      <input value={z.nombre} maxLength={60} onChange={(e) => cambiar(z.id, { nombre: e.target.value })} className="flex-1 min-w-0 text-sm border border-gray-200 rounded-lg px-2 py-1" aria-label="Nombre de la zona" />
                      <button type="button" onClick={() => setZonas(zonas.filter((x) => x.id !== z.id))} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Borrar zona"><Trash2 size={15} /></button>
                    </div>
                    <div className="flex gap-1">
                      {['alto', 'medio'].map((n) => (
                        <button key={n} type="button" onClick={() => cambiar(z.id, { nivel: n })} className={`flex-1 text-xs font-semibold rounded-lg px-2 py-1 border ${z.nivel === n ? 'text-white' : 'text-gray-600 bg-white border-gray-200'}`} style={z.nivel === n ? { background: COLOR_NIVEL[n], borderColor: COLOR_NIVEL[n] } : undefined}>
                          Riesgo {n}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap px-4 sm:px-5 py-3 border-t border-gray-200">
          <span className="text-xs text-red-600">{error}</span>
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 text-sm px-4 py-2">Cancelar</button>
            <button type="button" disabled={guardando || !!dibujo} onClick={guardar} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">
              {guardando && <Loader2 size={15} className="animate-spin" />}Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
